/**
 * Session Restore Hook
 *
 * Validates token on app load to restore authenticated session.
 * This enables persistent login — sessions survive tab/browser close.
 *
 * Backend is source of truth for session validity.
 *
 * Failure semantics (critical):
 *   - Auth failure (401/403/SESSION_EXPIRED) → full logout via orchestrator
 *   - Transport / network / unknown error   → show retry UI,
 *     leave localStorage intact so user can manually retry
 *
 * Uses SessionManager singleton for abort semantics — the authOrchestrator
 * calls sessionManager.cancelRestore() to prevent a late-arriving
 * verifyToken response from overriding a logout.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * BABEL-ESLINT CONSTRAINT (CRA 4 / react-scripts 4.0.1)
 *
 * CRA's webpack ESLintPlugin uses eslint-config-react-app/base.js which
 * sets parser: "babel-eslint". The @typescript-eslint/parser override for
 * .ts files is in the MAIN config — but CRA only extends base.js.
 *
 * This means babel-eslint parses ALL files (.js, .ts, .tsx). It handles
 * type annotations, interfaces, and generics — but NOT:
 *   - `as const` (const assertions)
 *   - `(x as Type)` (expression-level type assertions)
 *   - `catch (e: any)` (typed catch parameters)
 *
 * Workarounds used in this codebase:
 *   - Instead of `as const` → export plain objects/arrays
 *   - Instead of `(x as any).foo` → assign to untyped variable first
 *   - Instead of `catch (e: any)` → `catch (e)` (implicit any)
 * ──────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useState, useCallback } from 'react';
import { useAppDispatch } from '../redux/hooks';
import { store } from '../redux/store';
import { verifyTokenDirect } from '../api/auth';
import {
  setAuthLifecycle,
  setAuthenticatedUser,
  updateAuthToken,
} from '../redux/dashboard/reducers/userSlice';
import { logout as authLogout } from '../utils/authOrchestrator';
import { sessionManager } from '../utils/SessionManager';
import { AUTH_STORAGE_KEYS } from '../constants/authKeys';

const AUTH_FAILURE_CODES = [401, 403];
const MAX_RETRIES = 4;
const BACKOFF_MS = [500, 1000, 2000, 4000];

const isDev = process.env.NODE_ENV === 'development';

function isAuthoritativeFailure(error) {
  const status = error?.response?.status;
  if (status && AUTH_FAILURE_CODES.includes(status)) return true;
  const code = error?.response?.data?.code;
  if (code === 'SESSION_EXPIRED' || code === 'RBAC_VERSION_MISMATCH') return true;
  return false;
}

/** True when localStorage suggests a session worth verifying (sync read for first paint). */
function hasPersistedSessionCredentials() {
  if (typeof window === 'undefined') return false;
  try {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    const userId = localStorage.getItem(AUTH_STORAGE_KEYS.USER_ID);
    return !!(token && userId);
  } catch {
    return false;
  }
}

/**
 * When a logged-in user opens a public marketing URL, defer verifyToken until after the next paint
 * so FCP/LCP are not competing with session network + Redux on the main thread.
 */
function shouldDeferSessionRestoreForMarketingFirstPaint(pathname) {
  if (!pathname || pathname === '') return false;
  if (pathname === '/') return true;
  if (pathname === '/jobs' || pathname.startsWith('/jobs/')) return true;
  const exact = new Set([
    '/for-candidates',
    '/for-recruiters',
    '/pricing',
    '/integrations',
    '/about',
    '/contact',
  ]);
  if (exact.has(pathname)) return true;
  if (pathname.startsWith('/ai-')) return true;
  if (pathname.startsWith('/candidate-application')) return true;
  if (pathname.startsWith('/transparent-hiring')) return true;
  if (pathname.startsWith('/reduce-time-to-hire')) return true;
  return false;
}

export const useSessionRestore = () => {
  const dispatch = useAppDispatch();
  const [isRestoring, setIsRestoring] = useState(hasPersistedSessionCredentials);
  const [sessionRestoreFailed, setSessionRestoreFailed] = useState(false);

  const restoreSession = useCallback(async () => {
    setSessionRestoreFailed(false);

    const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
    const userId = localStorage.getItem(AUTH_STORAGE_KEYS.USER_ID);

    if (!token || !userId) {
      if (isDev) {
        console.warn('[session-restore] Missing token or userId:', { hasToken: !!token, hasUserId: !!userId });
      }
      dispatch(setAuthLifecycle('UNAUTHENTICATED'));
      setIsRestoring(false);
      return;
    }

    setIsRestoring(true);
    dispatch(setAuthLifecycle('RESTORING'));

    // Get abort signal from SessionManager
    const signal = sessionManager.startRestore();

    let lastError = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // Check if restore was cancelled (e.g., user clicked logout)
      if (signal.aborted) {
        if (isDev) {
          console.warn('[session-restore] Restore cancelled by logout');
        }
        setIsRestoring(false);
        return;
      }

      try {
        const response = await verifyTokenDirect(token);

        // Re-check lifecycle AFTER the async call — logout may have won the race
        const currentLC = store.getState().userReducer?.user?.authLifecycle;
        if (currentLC === 'LOGGING_OUT' || currentLC === 'UNAUTHENTICATED') {
          if (isDev) {
            console.warn('[session-restore] Logout occurred during restore — aborting');
          }
          setIsRestoring(false);
          return;
        }

        if (signal.aborted) {
          setIsRestoring(false);
          return;
        }

        const user = response?.user || response?.data?.user;

        if (user) {
          // Backend returns canonical session — no manual assembly.
          dispatch(
            setAuthenticatedUser({
              ...user,
              id: user._id || userId,
              token,
              authStatus: true,
              authLifecycle: 'AUTHENTICATED',
            }),
          );
          // updateAuthToken is NOT needed — token is in setAuthenticatedUser payload
          lastError = null;
          break;
        } else {
          // Backend returned 200 but no user — treat as auth failure
          if (isDev) {
            console.warn('[session-restore] 200 OK but no user in response:', {
              hasUser: !!response?.user,
              hasDataUser: !!response?.data?.user,
              responseKeys: response ? Object.keys(response) : [],
            });
          }
          setIsRestoring(false);
          authLogout(dispatch, { mode: 'hard', reason: 'session_expired' });
          return;
        }
      } catch (err) { // eslint: untyped — babel-eslint constraint
        lastError = err;

        // Check for abort
        if (err?.name === 'AbortError' || signal.aborted) {
          setIsRestoring(false);
          return;
        }

        if (isAuthoritativeFailure(err)) {
          // Backend explicitly rejected the token — full logout
          if (isDev) {
            console.warn('[session-restore] Auth failure — logging out:', {
              status: err?.response?.status,
              code: err?.response?.data?.code,
              message: err?.message,
            });
          }
          setIsRestoring(false);
          authLogout(dispatch, { mode: 'hard', reason: 'session_expired' });
          return;
        }
        // Transient error — retry if attempts remain
        if (attempt < MAX_RETRIES) {
          const delay = BACKOFF_MS[attempt - 1] ?? 500 * attempt;
          if (isDev) {
            console.warn('[session-restore] Transient failure, retrying:', attempt, '/', MAX_RETRIES, err?.message);
          }
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }

    if (lastError) {
      // All retries exhausted — show retry UI instead of clearing token
      if (isDev) {
        // Assign to untyped var — babel-eslint can't parse `(x as any)` assertions
        var lastErr = lastError;
        console.warn('[session-restore] Transient failure after retries:', {
          status: lastErr?.response?.status,
          message: lastErr?.message,
        });
      }
      setSessionRestoreFailed(true);
    }

    setIsRestoring(false);
  }, [dispatch]);

  useEffect(() => {
    const defer =
      typeof window !== 'undefined' &&
      hasPersistedSessionCredentials() &&
      shouldDeferSessionRestoreForMarketingFirstPaint(window.location.pathname || '');

    if (!defer) {
      restoreSession();
      return undefined;
    }

    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) restoreSession();
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [restoreSession]);

  return { isRestoring, sessionRestoreFailed, retryRestore: restoreSession };
};
